/* eslint-disable react-refresh/only-export-components */
import { useState, useEffect, useCallback, createContext, useContext, useRef, lazy, Suspense, startTransition } from "react";
import { createPortal } from "react-dom";
import { useTheme } from "../contexts/ThemeContext";
import { useDebug } from "../contexts/DebugContext";
import "./DeveloperMode.css";

const DevModeBackdoor = lazy(() => import("./DevModeBackdoor"));

// ---------------------------------------------------------------------------
// Context types, context object, and consumer hook for Developer Mode
// ---------------------------------------------------------------------------

/** Shape of the dev mode context exposed to consumers */
interface DevModeState {
  unlocked: boolean;
  lock: () => void;
  registerLogoClick: () => void;
  showLogoTooltipMessage: string | null;
  logoTooltipLong: boolean;
  timeRemainingSecs: number | null;
  logoWiggle: boolean;
  triggerLogoNudge: (message: string) => void;
  commentTipShown: boolean;
  markCommentTipShown: () => void;
}

/** React context for developer mode state */
const DevModeCtx = createContext<DevModeState>({
  unlocked: false,
  lock: () => {},
  registerLogoClick: () => {},
  showLogoTooltipMessage: null,
  logoTooltipLong: false,
  timeRemainingSecs: null,
  logoWiggle: false,
  triggerLogoNudge: () => {},
  commentTipShown: false,
  markCommentTipShown: () => {},
});

/** Hook to access dev mode context from any child component */
export const useDevMode = () => useContext(DevModeCtx);

/** Monotonically incrementing ID for toast notifications */
let nextToastId = 0;

/** Individual toast item component to handle exit transitions before unmounting. */
const DevToastItem = ({ text, onClosed }: { text: string; onClosed: () => void }) => {
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    // Schedule exit animation to start after 1.25s (leaves 0.25s for transition to reach 1.5s total)
    const exitTimer = setTimeout(() => {
      setIsExiting(true);
    }, 1250);
    return () => clearTimeout(exitTimer);
  }, []);

  return (
    <div
      className={`dev-toast ${isExiting ? "animate-out" : "animate-in"}`}
      onAnimationEnd={() => {
        if (isExiting) {
          onClosed();
        }
      }}
    >
      <span className="dev-toast-text">{text}</span>
    </div>
  );
};

// ---------------------------------------------------------------------------
// ConfettiCanvas - Full-screen canvas explosion on unlock
// ---------------------------------------------------------------------------

/** Canvas-based confetti burst that plays on unlock. Auto-cleans after animation. */
const ConfettiCanvas = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    /** Each confetti piece: position, velocity, color, rotation, and fade */
    const pieces: Array<{
      x: number; y: number; vx: number; vy: number;
      color: string; size: number; rotation: number;
      rotationSpeed: number; life: number; decay: number;
    }> = [];

    const colors = ["#00f0ff", "#00c3ff", "#69f0ae", "#a78bfa", "#ffffff", "#ff6b9d"];
    const isMobile = window.innerWidth < 768;
    const particleCount = isMobile ? 30 : 50;

    for (let i = 0; i < particleCount; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = Math.random() * 6 + 3;
      pieces.push({
        x: canvas.width / 2 + (Math.random() - 0.5) * 150,
        y: canvas.height / 2 - 50,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 3,
        color: colors[Math.floor(Math.random() * colors.length)],
        size: Math.random() * 6 + 4,
        rotation: Math.random() * 360,
        rotationSpeed: (Math.random() - 0.5) * 10,
        life: 1,
        decay: Math.random() * 0.012 + 0.006,
      });
    }

    let alive = true;

    /** Animation loop: update physics, draw pieces, stop when all have faded */
    const animate = () => {
      if (!alive) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      let anyAlive = false;
      for (const p of pieces) {
        if (p.life <= 0) continue;
        anyAlive = true;

        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.15;
        p.vx *= 0.99;
        p.rotation += p.rotationSpeed;
        p.life -= p.decay;

        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rotation * Math.PI) / 180);
        ctx.globalAlpha = Math.max(0, p.life);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
        ctx.restore();
      }

      if (anyAlive) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);

    /** Keep canvas dimensions in sync with viewport on resize */
    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    window.addEventListener("resize", handleResize);

    return () => {
      alive = false;
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="dev-confetti-canvas"
    />
  );
};

// ---------------------------------------------------------------------------
// UnlockOverlay - Celebration card with spring animation
// ---------------------------------------------------------------------------

/** Full-screen overlay with a spring-animated card celebrating dev mode unlock. Auto-dismisses after 3.5s. */
const UnlockOverlay = ({ onDone }: { onDone: () => void }) => {
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      setIsExiting(true);
    }, 3500);
    return () => clearTimeout(t);
  }, []);

  return (
    <div
      className={`dev-unlock-overlay ${isExiting ? "animate-out" : "animate-in"}`}
      onAnimationEnd={(e) => {
        if (e.target === e.currentTarget && isExiting) {
          onDone();
        }
      }}
    >
      <div className="dev-unlock-card">
        <div className="dev-unlock-icon">
          <span className="dev-unlock-emoji">🛡️</span>
        </div>
        <div className="dev-unlock-title">Developer Mode Unlocked</div>
        <div className="dev-unlock-subtitle">You found the secret, Mohit. Now go break things.</div>
        <div className="dev-unlock-bar" />
        <div className="dev-unlock-hint">Press ESC anytime to lock</div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// LockOverlay - Lock confirmation card with spring animation
// ---------------------------------------------------------------------------

/** Full-screen overlay with a spring-animated card confirming dev mode was locked. Auto-dismisses after 1.9s. */
const LockOverlay = ({ onDone }: { onDone: () => void }) => {
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      setIsExiting(true);
    }, 1900);
    return () => clearTimeout(t);
  }, []);

  return (
    <div
      className={`dev-lock-overlay ${isExiting ? "animate-out" : "animate-in"}`}
      onAnimationEnd={(e) => {
        if (e.target === e.currentTarget && isExiting) {
          onDone();
        }
      }}
    >
      <div className="dev-lock-card">
        <div className="dev-lock-icon">
          <span className="dev-lock-emoji">🔒</span>
        </div>
        <div className="dev-lock-title">Developer Mode Locked</div>
        <div className="dev-lock-subtitle">See you next time, hacker!</div>
        <div className="dev-lock-bar" />
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------
export const DevModeProvider = ({ children }: { children: React.ReactNode }) => {
  const { theme, setTheme } = useTheme();
  const { config: debugConfig } = useDebug();

  // -- State --

  /** Whether dev mode is unlocked. Not persisted to localStorage so it
      auto-locks on page refresh (prevents forgetting it's active). */
  const [unlocked, setUnlocked] = useState(false);

  /** Toast notifications exclusive to unlock flow (rendered above other toasts) */
  const [unlockToasts, setUnlockToasts] = useState<{ id: number; text: string }[]>([]);
  /** Single progress toast for clicking progress (updates in-place) */
  const [progressToast, setProgressToast] = useState<{ clicks: number; max: number } | null>(null);
  /** Whether the lock confirmation overlay is visible */
  const [showLockOverlay, setShowLockOverlay] = useState(false);
  /** Whether the unlock celebration overlay is visible */
  const [showUnlockOverlay, setShowUnlockOverlay] = useState(false);
  /** Message to display as tooltip near the MA logo */
  const [showLogoTooltipMessage, setShowLogoTooltipMessage] = useState<string | null>(null);
  /** Whether the auto tooltip is in its long duration phase (8-10 seconds) with jiggle */
  const [logoTooltipLong, setLogoTooltipLong] = useState(false);
  /** Version counter bumped on each logo click; triggers the idle reminder effect */
  const [reminderVersion, setReminderVersion] = useState(0);
  /** Whether the MA logo is currently wiggling/pulsing */
  const [logoWiggle, setLogoWiggle] = useState(false);
  /** Whether the developer comment tip has already been shown in this unlocked session */
  const [commentTipShown, setCommentTipShown] = useState(false);

  /** Mark the comment tip as shown in the current session */
  const markCommentTipShown = useCallback(() => {
    setCommentTipShown(true);
  }, []);

  // Reset comment tip shown state when locked/unlocked
  useEffect(() => {
    if (unlocked) {
      startTransition(() => setCommentTipShown(false));
    }
  }, [unlocked]);

  // Timers as refs so we can clear/schedule them across handlers, visibility listeners, and nudge triggers.
  const autoTeaserTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const visibilityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const nudgeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const idleIntervalRef = useRef(15000);

  // Clears all active automatic tooltip timers and tooltip states
  const clearAutoTooltipTimers = useCallback(() => {
    if (autoTeaserTimerRef.current) {
      clearTimeout(autoTeaserTimerRef.current);
      autoTeaserTimerRef.current = null;
    }
    if (visibilityTimerRef.current) {
      clearTimeout(visibilityTimerRef.current);
      visibilityTimerRef.current = null;
    }
    if (nudgeTimerRef.current) {
      clearTimeout(nudgeTimerRef.current);
      nudgeTimerRef.current = null;
    }
  }, []);

  const loopIdleTeaser = useCallback(function loopIdleTeaserFn() {
    if (unlockedRef.current || autoIdleCancelledRef.current) return;

    const isFirst = !hasShownFirstIdleTeaserRef.current;
    hasShownFirstIdleTeaserRef.current = true;

    setLogoTooltipLong(isFirst);
    setShowLogoTooltipMessage("> Don't click me");

    const duration = isFirst ? 9000 : 3000;

    autoTeaserTimerRef.current = setTimeout(() => {
      setShowLogoTooltipMessage(null);
      setLogoTooltipLong(false);
      
      if (!autoIdleCancelledRef.current && !unlockedRef.current) {
        autoTeaserTimerRef.current = setTimeout(loopIdleTeaserFn, idleIntervalRef.current);
      }
    }, duration);
  }, []);

  /** Programmatically triggers a nudge animation and tooltip on the MA logo */
  const triggerLogoNudge = useCallback((msg: string) => {
    if (unlockedRef.current) return;
    const hasClickedMaBefore = (() => {
      try { return localStorage.getItem("ma_clicked") === "1"; } catch { return false; }
    })();
    if (hasClickedMaBefore) return;

    // Clear any active idle teaser/visibility timers first
    clearAutoTooltipTimers();

    setLogoTooltipLong(true);
    setShowLogoTooltipMessage(msg);
    setLogoWiggle(true);
    setTimeout(() => {
      setLogoWiggle(false);
    }, 1500);

    // Keep nudge open for 9 seconds
    nudgeTimerRef.current = setTimeout(() => {
      setShowLogoTooltipMessage(null);
      setLogoTooltipLong(false);
      
      // Schedule the next idle teaser to start after current idle delay completes
      if (!autoIdleCancelledRef.current && !unlockedRef.current) {
        autoTeaserTimerRef.current = setTimeout(loopIdleTeaser, idleIntervalRef.current);
      }
    }, 9000);
  }, [clearAutoTooltipTimers, loopIdleTeaser]);

  // -- Refs --

  /** Number of consecutive logo clicks within the reset window.
      Uses a ref (not state) to avoid re-rendering on every click.
      Only the 5th click (unlock) triggers a re-render via setUnlocked. */
  const clickCountRef = useRef(0);
  /** Timer that resets clickCountRef after 1.5s of inactivity.
      1500ms balances two risks: too short frustrates deliberate multi-tap sequences,
      too long allows accidental unlocks from slow or distracted clicking. */
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Guards against re-entering lock/unlock while an overlay animation is playing.
      Without this guard, rapid Escape presses or logo clicks could stack multiple
      overlays on top of each other, breaking the animation flow and leaving
      isAnimatingRef stuck at true on edge cases. */
  const isAnimatingRef = useRef(false);
  /** Kept in sync with `unlocked` so callbacks always read the current value */
  const unlockedRef = useRef(unlocked);
  /** Cancels the auto "Don't click me" popup once the user has started clicking */
  const autoIdleCancelledRef = useRef(false);
  /** Tracks whether the first automatic idle teaser has already been shown in the session */
  const hasShownFirstIdleTeaserRef = useRef(false);

  // -- Side effects --

  /** Keep refs in sync so callbacks always read current values */
  useEffect(() => {
    unlockedRef.current = unlocked;
  }, [unlocked]);

  /** Toggle dev-mode CSS class on <html> so CSS can reliably hide/show dev elements */
  useEffect(() => {
    if (unlocked) {
      document.documentElement.classList.add('dev-mode');
    } else {
      document.documentElement.classList.remove('dev-mode');
    }
  }, [unlocked]);

  // -- Toast system --

  /** Push a toast notification; individual DevToastItem handles its own timeout and exit transitions */
  const addToast = useCallback((text: string) => {
    const id = ++nextToastId;
    setUnlockToasts((prev) => [...prev, { id, text }]);
  }, []);

  // -- Lock handler --

  /** Lock dev mode, revert hacker themes to saved pre-unlock theme, and show the lock overlay for 2s.
      Saves the current normal theme to ma_theme_before_dev when switching to matrix/cyberpunk-pink
      via the terminal, then restores it on lock. Falls back to "default" if no saved theme found. */
  const lock = useCallback(() => {
    if (isAnimatingRef.current) return;
    // Revert matrix/cyberpunk-pink to the saved pre-unlock theme when locking dev mode
    if (theme === "matrix" || theme === "cyberpunk-pink") {
      try {
        const before = localStorage.getItem("ma_theme_before_dev");
        if (before === "light" || before === "default") {
          setTheme(before);
        } else {
          setTheme("default");
        }
        localStorage.removeItem("ma_theme_before_dev");
      } catch {
        setTheme("default");
      }
    }
    setUnlocked(false);
    unlockedRef.current = false;
    clickCountRef.current = 0;
    setProgressToast(null);
    setShowLockOverlay(true);
    isAnimatingRef.current = true;
    autoIdleCancelledRef.current = false;
    hasShownFirstIdleTeaserRef.current = false;
    idleIntervalRef.current = 15000;
    // Dual onDone: parent timeout + LockOverlay child onDone both reset isAnimatingRef.
    // Redundancy prevents a stuck animation guard if the overlay fails to mount.
    setTimeout(() => {
      setShowLockOverlay(false);
      isAnimatingRef.current = false;
    }, 2400);
    try {
      localStorage.removeItem("ma_clicked");
    } catch { /* silent */ }
  }, [theme, setTheme]);

  // -- 5-click unlock handler --

  /**
   * Register a click on the MA logo. If 5 clicks happen within 1.5s, unlock.
   * Resets the click counter after inactivity. Shows progress toasts along the way.
   *
   * autoIdleCancelledRef prevents the idle "Don't click me" popup from showing
   * once the user starts clicking, so the teaser does not compete with real clicks.
   * The 1.5s reset timer prevents accidental unlocks from slow/distracted multi-tap:
   * if the user pauses too long between clicks, the count resets.
   */
  const registerLogoClick = useCallback(() => {
    if (unlockedRef.current || isAnimatingRef.current) return;
    autoIdleCancelledRef.current = true; // Once user engages, suppress idle teaser
    try { localStorage.setItem("ma_clicked", "1"); } catch { /* noop */ }

    // Clear active nudge / tooltip states when user manually interacts
    clearAutoTooltipTimers();
    setShowLogoTooltipMessage(null);
    setLogoTooltipLong(false);

    // Increase idle delay to 60 seconds so it doesn't spam the user
    idleIntervalRef.current = 60000;

    if (resetTimer.current) clearTimeout(resetTimer.current);
    resetTimer.current = setTimeout(() => {
      clickCountRef.current = 0;
      setProgressToast(null);

      // Once click sequence resets, allow idle teaser loop again with longer delay
      autoIdleCancelledRef.current = false;
      if (!unlockedRef.current) {
        autoTeaserTimerRef.current = setTimeout(loopIdleTeaser, idleIntervalRef.current);
      }
    }, 3000);

    clickCountRef.current += 1;
    const current = clickCountRef.current;

    if (current >= 5) {
      setUnlocked(true);
      unlockedRef.current = true;
      isAnimatingRef.current = true;
      setShowUnlockOverlay(true);
      try {
        localStorage.setItem("ma_dev_mode_opened_once", "1");
      } catch { /* noop */ }
      setProgressToast(null);
      addToast("🎉 Developer Mode Unlocked!");
      clickCountRef.current = 0;
      // Dual onDone: parent timeout + UnlockOverlay child onDone both reset isAnimatingRef.
      // If the overlay component fails to render or crashes, this parent timeout ensures
      // the animation guard does not get stuck at true permanently.
      setTimeout(() => {
        setShowUnlockOverlay(false);
        isAnimatingRef.current = false;
      }, 4000);
    } else {
      setProgressToast({ clicks: current, max: 5 });
      setReminderVersion((v) => v + 1);
    }
  }, [addToast, clearAutoTooltipTimers, loopIdleTeaser]);

  // -- Expiration policy for Developer Mode state (15 days of visit inactivity) --
  useEffect(() => {
    try {
      const lastSeenStr = localStorage.getItem("ma_dev_mode_last_seen");
      if (lastSeenStr) {
        const lastSeen = parseInt(lastSeenStr, 10);
        const diff = Date.now() - lastSeen;
        const EXPIRY_TIME_MS = 15 * 24 * 60 * 60 * 1000; // 15 days
        if (diff > EXPIRY_TIME_MS) {
          localStorage.removeItem("ma_dev_mode_opened_once");
          localStorage.removeItem("ma_clicked");
        }
      }
      localStorage.setItem("ma_dev_mode_last_seen", Date.now().toString());
    } catch { /* ignore private storage blocks */ }
  }, []);

  // -- Keyboard shortcut: Escape to lock --
  // Only fires when unlocked AND not animating. The isAnimatingRef guard
  // prevents Escape from interrupting a lock/unlock overlay transition,
  // which would leave isAnimatingRef stuck at true (no way to unstick it).
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && unlockedRef.current && !isAnimatingRef.current) {
        e.preventDefault();
        lock();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lock]);

  // -- Auto "Don't click me" popup every 15s --
  /** Idle teaser: shows "Don't click me" near the MA logo every 15s until the user engages. */
  useEffect(() => {
    if (unlocked) return;
    const hasOpenedDevModeBefore = (() => {
      try { return localStorage.getItem("ma_dev_mode_opened_once") === "1"; } catch { return false; }
    })();
    if (hasOpenedDevModeBefore) {
      autoIdleCancelledRef.current = true;
      return;
    }
    autoIdleCancelledRef.current = false;

    // Start initial teaser timer
    autoTeaserTimerRef.current = setTimeout(loopIdleTeaser, 15000);

    return () => {
      clearAutoTooltipTimers();
    };
  }, [unlocked, loopIdleTeaser, clearAutoTooltipTimers]);

  // Handle Tab Visibility Change (Pause/Resume Teaser)
  useEffect(() => {
    if (unlocked) return;
    const hasOpenedDevModeBefore = (() => {
      try { return localStorage.getItem("ma_dev_mode_opened_once") === "1"; } catch { return false; }
    })();
    if (hasOpenedDevModeBefore) return;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab went to background: pause and clear active timers/tooltips
        clearAutoTooltipTimers();
        setShowLogoTooltipMessage(null);
        setLogoTooltipLong(false);
      } else {
        // Tab returned to foreground: wait 2.5s and show teaser instantly if not already clicked/unlocked
        const hasClickedMaBefore = (() => {
          try { return localStorage.getItem("ma_clicked") === "1"; } catch { return false; }
        })();
        if (hasClickedMaBefore || unlockedRef.current || autoIdleCancelledRef.current) return;

        visibilityTimerRef.current = setTimeout(() => {
          if (unlockedRef.current || autoIdleCancelledRef.current) return;

          setLogoTooltipLong(true);
          setShowLogoTooltipMessage("> Don't click me");

          // Keep visible for 9s, then schedule next idle teaser 15s later
          autoTeaserTimerRef.current = setTimeout(() => {
            setShowLogoTooltipMessage(null);
            setLogoTooltipLong(false);

            if (!autoIdleCancelledRef.current && !unlockedRef.current) {
              autoTeaserTimerRef.current = setTimeout(loopIdleTeaser, idleIntervalRef.current);
            }
          }, 9000);
        }, 2500);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [unlocked, loopIdleTeaser, clearAutoTooltipTimers]);

  // -- Reminder: "X clicks left..." after 5s idle --
  /** After a logo click, wait 4s of idle and then show how many clicks remain (or "Tap 5 times fast!") */
  useEffect(() => {
    if (unlocked || reminderVersion === 0) return;
    const count = clickCountRef.current;
    if (count === 0) return;
    const timer = setTimeout(() => {
      const expired = clickCountRef.current === 0;
      const msg = expired
        ? "Tap 5 times fast!"
        : `${5 - clickCountRef.current} click${5 - clickCountRef.current > 1 ? "s" : ""} left...`;
      setShowLogoTooltipMessage(msg);
      setTimeout(() => setShowLogoTooltipMessage(null), 3000);
    }, 4000);
    return () => clearTimeout(timer);
  }, [reminderVersion, unlocked]);

  // -- Init effect: revert hacker themes on refresh --
  /** On mount, if dev mode is not unlocked but the theme is matrix/cyberpunk-pink
      (e.g. page refreshed while dev mode was active), restore the saved pre-unlock
      theme from localStorage and clear the saved key. Falls back to "default" if
      no saved theme or invalid value. */
  useEffect(() => {
    if (unlocked) return;
    if (theme !== "matrix" && theme !== "cyberpunk-pink") return;
    try {
      const before = localStorage.getItem("ma_theme_before_dev");
      if (before === "light" || before === "default") {
        setTheme(before);
      } else {
        setTheme("default");
      }
      localStorage.removeItem("ma_theme_before_dev");
    } catch {
      setTheme("default");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -- Auto-lock timer from config --
  /** Countdown seconds for UI display. Starts at debugConfig.DEV_MODE_TIMER minutes when unlocked, counts down to 0, then auto-locks. */
  const [timeRemainingSecs, setTimeRemainingSecs] = useState<number | null>(null);

  /** Ref-based lock function to avoid timer effect re-firing when lock changes */
  const lockRef = useRef(lock);
  useEffect(() => { lockRef.current = lock; }, [lock]);

  /** When unlocked, start countdown based on DEV_MODE_TIMER config. At 0, auto-lock. Resets on re-lock (effect cleanup).
      Re-reads config on each unlock so debug panel changes take effect without refresh. */
  useEffect(() => {
    if (!unlocked) {
      setTimeRemainingSecs(null);
      return;
    }

    const totalSecs = Math.round((debugConfig.DEV_MODE_TIMER || 15) * 60);
    setTimeRemainingSecs(totalSecs);
    const interval = setInterval(() => {
      setTimeRemainingSecs((prev) => {
        if (prev === null || prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [unlocked, debugConfig.DEV_MODE_TIMER]);

  /** Watch for timer expiry at 0 and lock dev mode. */
  useEffect(() => {
    if (timeRemainingSecs === 0) {
      lock();
    }
  }, [timeRemainingSecs, lock]);

  return (
    <DevModeCtx.Provider value={{ unlocked, lock, registerLogoClick, showLogoTooltipMessage, logoTooltipLong, timeRemainingSecs, logoWiggle, triggerLogoNudge, commentTipShown, markCommentTipShown }}>
      {/* Prevent scroll ONLY during overlay animations.
          Inline <style> tag used instead of a CSS class because the style must apply
          synchronously BEFORE the browser paints the overlay. CSS class application
          depends on React rendering + commit phase timing, which could lag and let
          scrollbars flicker into view during the unlock animation. */}
      {(showUnlockOverlay || showLockOverlay) && (
        <style>{`
          html, body {
            overflow: hidden !important;
          }
        `}</style>
      )}

      {children}

      {/* Unlock toasts - z-index ABOVE the popup */}
      <div className="dev-toast-container dev-toast--above">
        {unlockToasts.map((t) => (
          <DevToastItem
            key={t.id}
            text={t.text}
            onClosed={() => {
              setUnlockToasts((prev) => prev.filter((item) => item.id !== t.id));
            }}
          />
        ))}
      </div>

      {/* Progress Toast (in-place update, bottom center) */}
      {progressToast && (
        <div className="dev-progress-toast-container">
          <div className="dev-progress-toast animate-in">
            <span className="dev-progress-toast-text">
              [ ROOT ACCESS: {progressToast.clicks}/{progressToast.max} ]&nbsp;&nbsp;
              [ {Array.from({ length: progressToast.max }, (_, i) => i < progressToast.clicks ? "█" : "░").join("")} ]
            </span>
          </div>
        </div>
      )}

      {/* Unlock celebration overlay */}
      {showUnlockOverlay && (
        <UnlockOverlay
          onDone={() => {
            setShowUnlockOverlay(false);
            isAnimatingRef.current = false;
          }}
        />
      )}

      {/* Lock confirmation overlay */}
      {showLockOverlay && (
        <LockOverlay
          onDone={() => {
            setShowLockOverlay(false);
            isAnimatingRef.current = false;
          }}
        />
      )}

      {/* Confetti canvas - above everything */}
      {showUnlockOverlay && <ConfettiCanvas />}

      {/* Lazy-loaded backdoor panel containing terminal */}
      {unlocked && (
        <Suspense fallback={null}>
          <DevModeBackdoor />
        </Suspense>
      )}
    </DevModeCtx.Provider>
  );
};

// ---------------------------------------------------------------------------
// HiddenDevComment - Sticky note pinned to top-right corner of parent
// ---------------------------------------------------------------------------

/** Props for a single hidden dev comment: the comment body, the reveal message (shown below), and optional icon */
interface HiddenDevCommentProps {
  text: string;
  reveal: string;
  icon?: string;
}

export const HiddenDevComment = ({
  text,
  reveal,
  icon = "💬",
}: HiddenDevCommentProps) => {
  const { unlocked, commentTipShown, markCommentTipShown } = useDevMode();
  const { config: debug } = useDebug();

  // -- Popup state --
  const [isOpen, setIsOpen] = useState(false);
  const [showFirstTimeTip, setShowFirstTimeTip] = useState(false);
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const badgeRef = useRef<HTMLSpanElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const [popupStyle, setPopupStyle] = useState<React.CSSProperties>({});

  // -- Click outside to close --
  /** Close the popup when user clicks anywhere outside both the wrapper and the portal popup */
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const insideWrapper = wrapperRef.current?.contains(target);
      const insidePortal = popupRef.current?.contains(target);
      if (!insideWrapper && !insidePortal) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  if (!unlocked || !debug.DEV_COMMENTS) {
    return null;
  }

  /** Toggle popup open/closed with viewport-aware positioning.
      Uses position:absolute with PAGE coordinates (viewport coords + scroll offset),
      so the popup is rendered in a portal at document.body level to escape stacking
      contexts/compositing layers, while still scrolling naturally with the page.

      getBoundingClientRect() returns viewport-relative coords. Adding window.scrollY/X
      converts them to document coordinates, which is what position:absolute on a
      body-level element needs. */
  const togglePopup = () => {
    if (!isOpen && badgeRef.current) {
      const badgeRect = badgeRef.current.getBoundingClientRect();
      const pw = Math.min(420, window.innerWidth - 32);
      const gap = 8;

      // Center popup horizontally under badge, clamped to viewport bounds
      let viewportLeft = badgeRect.left + (badgeRect.width - pw) / 2;
      if (viewportLeft < 12) viewportLeft = 12;
      if (viewportLeft + pw > window.innerWidth - 12) viewportLeft = window.innerWidth - pw - 12;

      // Convert viewport coords → document/page coords by adding scroll offsets.
      // position:absolute on a body child uses the document origin (not viewport).
      const top = badgeRect.bottom + window.scrollY + gap;
      const left = viewportLeft + window.scrollX;

      setPopupStyle({
        position: "absolute",
        top,
        left,
        width: pw,
        transformOrigin: "top center",
      });

      // Check if this is the first time the user opens a comment in this session
      if (!commentTipShown) {
        setShowFirstTimeTip(true);
        markCommentTipShown();
      } else {
        setShowFirstTimeTip(false);
      }
    }
    setIsOpen((prev) => !prev);
  };

  // Portal popup element, rendered into document.body to escape all stacking contexts
  const popupEl = (
    <div
      ref={popupRef}
      className={`dev-comment-popup ${isOpen ? "is-open" : ""}`}
      style={popupStyle}
    >
      <button
        type="button"
        className="dev-comment-popup-close"
        onClick={() => setIsOpen(false)}
        data-cursor-tooltip="Close"
      />

      <div className="dev-comment-popup-content">

        {showFirstTimeTip && (
          <div className="dev-comment-first-time-tip">
            <span className="dev-comment-tip-icon">🚨</span>
            <div className="dev-comment-tip-text">
              <strong>DEVELOPER WARNING:</strong> Code commentary active. You are viewing hidden source comments. Click bubbles to reveal underlying logic details!
            </div>
          </div>
        )}

        <div className="dev-comment-popup-code">
          <span className="dev-comment-tag">&lt;!-- </span>
          <span className="dev-comment-text">{text}</span>
          <span className="dev-comment-tag"> --&gt;</span>
        </div>
        <div className="dev-comment-popup-reveal">{reveal}</div>
      </div>
    </div>
  );

  return (
    <>
      {/* Badge inline in the document flow */}
      <span className="dev-comment-wrapper" ref={wrapperRef}>
        <span
          ref={badgeRef}
          className="dev-comment-badge"
          onClick={togglePopup}
          data-tooltip="View developer comment"
        >
          <span className="dev-comment-icon">{icon}</span>
        </span>
      </span>

      {/* Popup rendered into document.body via portal, escaping all stacking contexts
          and compositing layers, fixing navbar overlap and transparent background bugs */}
      {createPortal(popupEl, document.body)}
    </>
  );
};
